import {
  Component,
  computed,
  effect,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  AUTHORIZE_PAYMENT,
  DIRECT_TICKET_ADD_PAYMENT,
  DIRECT_TICKET_CART_KEY,
  EEntityType,
  EOrderOrigin,
  ERelationType,
  IBuyResponse,
  IPaymentPreference,
  IPurchase,
  PaymentMethod,
  USER_GUEST_ID,
} from '@web/dtos';
import { PaymentPreferenceStore } from '../../../../services/api/user/paymentPreference/payment-preference-store.service';
import { PaymentProviderStore } from '../../../../services/api/payment-provider/payment-provider-store.service';
import { PaymentMethodDisplayComponent } from '../../../../components/data-display/payment-method-display/payment-method-display.component';
import { ShoppingCartService } from '../../../../services/shopping-cart/shopping-cart.service';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { JsonPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { IApiResponse } from '@web/shared-types';
import { AuthProviderFactory } from '../../../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../../../services/auth/auth.provider';
import { UserStore } from '../../../../services/api/user/user/user-store.service';

@Component({
  selector: 'aw-direct-ticket-payment-page',
  imports: [
    PaymentMethodDisplayComponent,
    ButtonComponent,
    LoadingAnimationComponent,
    JsonPipe,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './direct-ticket-payment-page.component.html',
})
export class DirectTicketPaymentPageComponent {
  protected readonly paymentPreferenceStore: PaymentPreferenceStore = inject(
    PaymentPreferenceStore
  );
  protected readonly paymentProviderStore: PaymentProviderStore =
    inject(PaymentProviderStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly shoppingCart: ShoppingCartService =
    inject(ShoppingCartService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  protected transactionId: string | undefined =
    this.route.snapshot.queryParams['datatransTrxId'];
  protected readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  protected userStore: UserStore = inject(UserStore);
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  private readonly paymentMethods: Signal<PaymentMethod[]> =
    this.paymentProviderStore.paymentMethods;
  protected readonly purchase: WritableSignal<any | undefined> = signal<
    IPurchase | undefined
  >(undefined);
  protected readonly isTransactionInProgress: WritableSignal<boolean> =
    signal<boolean>(false);
  protected readonly redirectAfterPaymentAuthorize: WritableSignal<boolean> =
    signal(false);
  protected readonly isLoadingPayment: Signal<boolean> = computed(
    (): boolean =>
      this.paymentPreferenceStore.isLoading() ||
      this.paymentProviderStore.isLoading() ||
      !this.paymentMethods()
  );
  protected readonly preferredPaymentMethod: Signal<PaymentMethod | undefined> =
    computed((): PaymentMethod | undefined => {
      const preferredPayment: IPaymentPreference | undefined =
        this.paymentPreferenceStore.paymentPreference();
      const paymentMethods: PaymentMethod[] = this.paymentMethods();
      if (preferredPayment && paymentMethods && paymentMethods.length > 0) {
        return (
          paymentMethods.find(
            (method: PaymentMethod): boolean =>
              method.id === preferredPayment.paymentMethodId
          ) ?? paymentMethods[0]
        );
      }
      return paymentMethods?.[0];
    });

  constructor() {
    if (localStorage.getItem(AUTHORIZE_PAYMENT)) {
      window.localStorage.removeItem(AUTHORIZE_PAYMENT);
      this.redirectAfterPaymentAuthorize.set(true);
    }
    effect((): void => {
      const hasAttemptedToAddPaymentMethod: string | null =
        localStorage.getItem(DIRECT_TICKET_ADD_PAYMENT);
      if (hasAttemptedToAddPaymentMethod && this.userStore.userOrGuestId()) {
        window.localStorage.removeItem(DIRECT_TICKET_ADD_PAYMENT);
        this.paymentPreferenceStore.loadPreference(
          this.userStore.userOrGuestId()
        );
      }
    });
    effect((): void => {
      if (this.userStore.user()?.userId) {
        this.paymentProviderStore.getPaymentMethods();
      }
    });
  }

  public placeOrder(paymentMethod: PaymentMethod): void {
    this.isTransactionInProgress.set(true);
    this.shoppingCart
      .orderDirectTicketsInCart(paymentMethod)
      .subscribe(result => {
        this.purchase.set(result);
        this.isTransactionInProgress.set(false);
        if (result.message?.params!['data']) {
          window.localStorage.setItem(AUTHORIZE_PAYMENT, String(true));
          window.location.href =
            result.message?.params!['data'].providerResponse.redirectUrl;
        }
      });
  }

  public addPaymentDirect(): void {
    window.localStorage.setItem(
      DIRECT_TICKET_ADD_PAYMENT,
      JSON.stringify(true)
    );
    window.localStorage.setItem(USER_GUEST_ID, this.userStore.userOrGuestId());
    window.localStorage.setItem(
      DIRECT_TICKET_CART_KEY,
      JSON.stringify({
        products: this.shoppingCart.products,
        personalData: this.shoppingCart.personalDataForm.getRawValue(),
      })
    );
    const request = this.userStore.user()
      ? this.paymentProviderStore.addPaymentMethods(
          EOrderOrigin.DirectTicketConfig,
          this.userStore.user(),
          this.shoppingCart.products
        )
      : this.paymentProviderStore.addGuestPaymentMethods(
          this.userStore.userOrGuestId(),
          EOrderOrigin.DirectTicketConfig,
          this.shoppingCart.products
        );
    request.subscribe(response => {
      window.location.href = response.data.redirectUrl;
    });
  }

  public placeGuestOrder(customerId: string): void {
    window.localStorage.setItem(
      DIRECT_TICKET_ADD_PAYMENT,
      JSON.stringify(true)
    );
    window.localStorage.setItem(USER_GUEST_ID, this.userStore.userOrGuestId());
    window.localStorage.setItem(
      DIRECT_TICKET_CART_KEY,
      JSON.stringify({
        products: this.shoppingCart.products,
        personalData: this.shoppingCart.personalDataForm.getRawValue(),
      })
    );
    this.isTransactionInProgress.set(true);
    this.shoppingCart
      .orderDirectTicketsInCartAsGuest(customerId)
      .subscribe((result: IApiResponse<IBuyResponse>) => {
        this.isTransactionInProgress.set(false);
        if (result.data) {
          window.location.href = result.data!.providerResponse!.redirectUrl;
        }
      });
  }
}
